import type { Middleware } from "@reduxjs/toolkit";
import type { QueryClient } from "@tanstack/react-query";
import { invalidateSeatCallerFacts } from "../../libs/Phone/utils/seatAvailabilityFreshness";
import {
  effectiveSessionUserId,
  type SessionState,
  sessionSlice,
} from "./session";

interface SessionCacheRootState {
  session: SessionState;
}

export const hasSession = (session: SessionState): boolean =>
  Boolean(session.user || session.accessToken);

const sessionIdentity = (session: SessionState): string | null => {
  const userId = effectiveSessionUserId(session);
  if (!userId) return null;

  return session.impersonation
    ? `impersonation:${session.impersonation.sessionId}:${userId}`
    : session.user
      ? `${session.user.accountId}:${userId}`
      : null;
};

const assignsFirstAccount = (
  previousSession: SessionState,
  nextSession: SessionState,
): boolean => {
  const previousUser = previousSession.user;
  const nextUser = nextSession.user;
  return previousUser?.id === nextUser?.id && previousUser?.accountId === "";
};

export const sessionIdentityChanged = (
  previousSession: SessionState,
  nextSession: SessionState,
): boolean => {
  if (previousSession === nextSession) return false;

  const previousIdentity = sessionIdentity(previousSession);
  const nextIdentity = sessionIdentity(nextSession);

  if (previousIdentity !== nextIdentity) {
    return true;
  }

  const previousHadToken = Boolean(previousSession.accessToken);
  const nextHasToken = Boolean(nextSession.accessToken);
  if (previousHadToken !== nextHasToken) {
    return true;
  }

  // A token without a user is an incomplete session, so there is no stable
  // identity to compare. Clear conservatively if another tab replaces it.
  return (
    previousIdentity === null &&
    previousSession.accessToken !== nextSession.accessToken
  );
};

/**
 * Clear all cached queries synchronously at authentication boundaries. Keeping
 * this in Redux middleware covers hook-driven logout, router-driven logout,
 * and cross-tab storage sync before React can render data from the prior
 * session. A `setUser` that gives an accountless user their first account is
 * not a boundary; every other user or account change is. Query removal deliberately
 * leaves mutation state alone because login is dispatched from mutation
 * `onSuccess` handlers. Silent same-user token refreshes use `tokenRefreshed`
 * and bypass this cleanup.
 */
export const createSessionCacheMiddleware =
  (queryClient: QueryClient): Middleware<unknown, SessionCacheRootState> =>
  ({ getState }) =>
  (next) =>
  (action) => {
    const previousSession = getState().session;
    const result = next(action);
    const nextSession = getState().session;

    const exemptSetUser =
      sessionSlice.actions.setUser.match(action) &&
      assignsFirstAccount(previousSession, nextSession);
    const identityChanged =
      !exemptSetUser && sessionIdentityChanged(previousSession, nextSession);
    const shouldClear =
      sessionSlice.actions.login.match(action) ||
      (identityChanged && hasSession(previousSession)) ||
      (sessionSlice.actions.logout.match(action) &&
        hasSession(previousSession));

    if (identityChanged || shouldClear) invalidateSeatCallerFacts();
    // Initial storage hydration has no outgoing actor/cache. Keep anonymous
    // bootstrap data; a replacement of any existing actor removes old queries.
    if (shouldClear) queryClient.removeQueries();

    return result;
  };
